/** What the toolbar's `.meta` line knows: the corpus total when the engine counts one, and the page's own range. */
export interface AGUResultCount {
    readonly Total: number | null;
    readonly Start: number;
    readonly End: number;
    readonly Noun: string;
    /**
     * True when `Total` is an estimate, not an exact count (a listing the engine read only part of the way) — the bold
     * value reads e.g. "about 15,199" (FormatApproximateCount). It can flip to exact as the member pages deeper: the
     * page whose read reaches the listing's end reports the exact count.
     */
    readonly Approximate?: boolean;
}

/** `person` → `people` (P/people-finder.html:1445 — "N people / 1 person"); every other finder noun just takes an `s`. */
function PluralOf(noun: string): string {
    return noun === 'person' ? 'people' : `${noun}s`;
}

/** The count line of an empty list, whatever the noun (P/proto-states.js:99, 102). */
export const ZERO_RESULTS = '0 results';

/** The one rendering of an approximate count, shared by the toolbar's count line and the tab badge: "about 15,199". */
export function FormatApproximateCount(n: number): string {
    return 'about ' + n.toLocaleString('en-US');
}

/**
 * The mockup's count line (grants.html:960): `<b>51</b> grants · showing 1 to 9`,
 * or `0 results` (P/proto-states.js:99, the empty state's `#count`). With no total (jobs — exact-K supply, no countable set) the
 * honest line is the range alone (D-F6).
 *
 * Three shapes, one per honest thing there is to say:
 *   - the search found nothing → `0 results` (no bold: there is no number worth
 *     emphasising);
 *   - the search found results and the page shows some → the mockup's line;
 *   - the search found results and the page shows NONE → `<b>117</b> grants ·
 *     0 shown`. The finder pages count what the COLUMN renders (final-review
 *     finding #1), and the `Show` switches filter the page client-side, so a
 *     counted search genuinely arrives here with a real total over an empty
 *     column. `PageResultCount` never zeroes `Start` — that is what keeps the
 *     null-total branch's `End < Start` zero-signal working — so without this
 *     case the counted branch printed the nonsense range `showing 1 to 0`. The
 *     total stays bold because it is still true; it is the page that is empty,
 *     and saying so is what tells the member the filter, not the search, is
 *     what emptied it (controller ruling, 2026-09-11).
 */
export function FormatCountLine(c: AGUResultCount): { Bold: string | null; Text: string } {
    const plural = PluralOf(c.Noun);
    if (c.Total !== null) {
        if (c.Total === 0) return { Bold: null, Text: ZERO_RESULTS };
        const noun = c.Total === 1 ? c.Noun : plural;
        // Grouped either way, so an exact 15,199 never reads `15199` beside an approximate "about 15,199".
        const bold = c.Approximate === true ? FormatApproximateCount(c.Total) : c.Total.toLocaleString('en-US');
        if (c.End < c.Start) return { Bold: bold, Text: ` ${noun} · 0 shown` };
        return { Bold: bold, Text: ` ${noun} · showing ${c.Start} to ${c.End}` };
    }
    if (c.End < c.Start) return { Bold: null, Text: ZERO_RESULTS };
    return { Bold: null, Text: `Showing ${c.Start} to ${c.End}` };
}

/**
 * Builds an `AGUResultCount` off one page's own paging math, shared by every
 * finder page's `ResultCount` getter (grants, people, jobs). `Start` is always
 * `(page-1)*pageSize + 1` — NEVER zeroed for an empty page — so that on a
 * zero-result page `End` (the page start, unmoved) falls strictly below
 * `Start`. `FormatCountLine` uses that (`End < Start`) as its only
 * zero-detection signal when `total` is `null` (jobs: exact-K supply, no
 * countable set) — a zeroed `Start` would instead produce `{Start:0,End:0}`,
 * which reads as a valid one-item range and prints "Showing 0 to 0" instead
 * of "0 jobs" (review finding, round 1).
 *
 * `Approximate` is only ever spread in as `true` — never `false` — so an
 * `AGUResultCount` built without this option (every exact call site,
 * including the grant/people finders' own 5-arg calls) still `toEqual`s
 * a literal with no `Approximate` key at all: a present-but-`false` field is a
 * defined value, not `undefined`, so `toEqual` would otherwise treat it as an
 * unexpected extra property and fail those callers' existing assertions.
 */
export function PageResultCount(total: number | null, page: number, pageSize: number, onPage: number, noun: string, approximate = false): AGUResultCount {
    const start = (page - 1) * pageSize;
    return { Total: total, Start: start + 1, End: start + onPage, Noun: noun, ...(approximate ? { Approximate: true } : {}) };
}
